/**
 * The Defender-for-Identity section of the Defender board (L1): sensor and agent
 * health, as counts.
 *
 * This is the panel PITG actually did — is the identity monitoring itself
 * working — and it is a point-in-time read, not a window. `formatWindow`
 * renders `PT0S` as exactly that, so nobody reads these figures as a 14-day
 * average.
 *
 * **Why there are no sensor names.** MDI identifies a sensor to Microsoft by its
 * DNS name, and a domain controller's DNS name is its identity. No model in the
 * payload has a field that can hold one, so this section counts sensors by state
 * and never lists them. That is the control: not a filter on the way out, but the
 * absence of anywhere to put one.
 *
 * **Why `unknown` counts as unhealthy.** The backend counts every state other
 * than `healthy` — `unknown` included — as unhealthy, because a sensor whose
 * state cannot be read is not a healthy sensor. The verdict card says so, so the
 * figure cannot be misread as pessimism.
 *
 * **Why a 404 here is a different ticket.** If MDI is not deployed, this route
 * answers `not_provisioned` rather than zeros — a fact about the estate, needing
 * a licensing or deployment decision, not a consent grant. That state never
 * reaches this component: `index.tsx` renders the access panel instead.
 *
 * **Why deployment status is charted separately from health status.** A sensor
 * mid-deployment can report `healthy` while monitoring nothing, so the health
 * mix alone hides an installer that never finished. It takes the categorical
 * palette rather than the status one: the vocabulary is Microsoft's and changes
 * without a deploy, so there is no honest mapping from it onto
 * good/degraded/broken, and inventing one is what §8 exists to forbid.
 *
 * **Why the oldest open issue has an age.** One health issue opened this morning
 * and one open since spring are the same count and a different problem, so the
 * age is published beside the count — mirroring `oldest_open_days` on the
 * incident panel, on the same 7-day edge. It is counted, never named: the
 * issue's `sensorDNSNames` never left the backend's projection.
 */

import { Icon } from "@norican/icons";
import { Badge, Statistic, Typography } from "@norican/react";

import type { DefenderIdentityHealth } from "@/lib/api";

import { DefenderErrorsAlert } from "./access-panel";
import { BreakdownPanel } from "./breakdown-panel";
import { PanelCard, SectionHeading } from "./panel-card";
import { IDENTITY_ANCHOR, TWIN_IDS } from "./params";
import {
  cautionTone,
  formatCount,
  formatDays,
  formatPercent,
  formatWindow,
  HEALTH_DETAIL,
  HEALTH_ICON,
  HEALTH_LABEL,
  HEALTH_TONE,
  oldestOpenTone,
  problemTone,
  STALE_INCIDENT_DAYS,
} from "./status";

interface IdentitySectionProps {
  identity: DefenderIdentityHealth;
}

export function IdentitySection({ identity }: IdentitySectionProps) {
  const staleAfterMs = identity.cache_ttl_sec * 2000;
  const since = identity.collected_at;
  const verdictTone = HEALTH_TONE[identity.state];

  // Coverage beside the number (§5): a healthy share is only meaningful against
  // how many sensors were actually read, and it is null when either half is.
  const healthyShare =
    identity.sensors_healthy != null && identity.sensors_total != null && identity.sensors_total > 0
      ? (identity.sensors_healthy / identity.sensors_total) * 100
      : null;

  return (
    <section id={IDENTITY_ANCHOR} className="flex scroll-mt-6 flex-col gap-4">
      <SectionHeading
        title="Identity sensor health"
        subtitle={`Defender for Identity's own sensors and agents, ${formatWindow(
          identity.window,
        )}. Counts by state only — a sensor is identified to Microsoft by its DNS name, which is a domain controller's identity, so no sensor is named anywhere on this board.`}
      />

      <DefenderErrorsAlert errors={identity.errors} />

      <PanelCard
        title="Verdict"
        caption={
          <span className="flex flex-wrap items-center gap-2">
            <Icon
              icon={HEALTH_ICON[identity.state]}
              size="sm"
              // `IconTone` has no `neutral` member, so the unknown verdict
              // deliberately carries no icon tone — it is named by the badge.
              tone={verdictTone === "neutral" ? undefined : verdictTone}
            />
            <Badge tone={verdictTone} variant="subtle" size="sm">
              {HEALTH_LABEL[identity.state]}
            </Badge>
          </span>
        }
      >
        <div className="flex flex-col gap-5">
          <Typography variant="body-sm" tone="muted">
            {HEALTH_DETAIL[identity.state]}
          </Typography>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
            <Statistic
              label="Sensors known"
              value={formatCount(identity.sensors_total)}
              tone={identity.sensors_total == null ? "neutral" : "accent"}
              accent
              caption={
                identity.sensors_total == null
                  ? "The sensor read failed — unknown, not zero sensors"
                  : identity.sensors_total === 0
                    ? "MDI answered and knows of no sensor. A measured zero — and a finding, since identity monitoring with no sensors monitors nothing."
                    : "Sensors MDI reported at this read"
              }
            />
            <Statistic
              label="Healthy"
              value={formatCount(identity.sensors_healthy)}
              // Healthy is the good answer, so it earns success only when it was
              // actually read and there is something to be healthy about.
              tone={
                identity.sensors_healthy == null
                  ? "neutral"
                  : identity.sensors_healthy > 0
                    ? "success"
                    : "warning"
              }
              accent
              progress={healthyShare ?? undefined}
              progressTone="success"
              caption={
                identity.sensors_healthy == null
                  ? "The sensor read failed — unknown, not zero healthy"
                  : healthyShare == null
                    ? "No coverage figure: the sensor total was not read, so this is a count without a denominator"
                    : `${formatPercent(healthyShare)} of the sensors that were read`
              }
            />
            <Statistic
              label="Not healthy"
              value={formatCount(identity.sensors_unhealthy)}
              tone={
                identity.sensors_unhealthy == null
                  ? "neutral"
                  : cautionTone(identity.sensors_unhealthy)
              }
              accent
              caption={
                identity.sensors_unhealthy == null
                  ? "The sensor read failed — unknown, not zero unhealthy"
                  : "Every state other than healthy, including unknown: a sensor whose state cannot be read is not a healthy sensor"
              }
            />
            <Statistic
              label="Open health issues"
              value={formatCount(identity.open_health_issues)}
              tone={problemTone(identity.open_health_issues)}
              accent
              caption={
                identity.open_health_issues == null
                  ? "The issues read failed — unknown, not zero open"
                  : identity.open_health_issues === 0
                    ? "MDI has no open health issue. A measured zero, and genuinely good news."
                    : "Issues MDI has open against the sensors above"
              }
            />
            <Statistic
              label="Oldest open issue"
              value={formatDays(identity.oldest_open_issue_days)}
              // Null has two opposite causes here, exactly as it does for the
              // oldest open incident, so the tone reads the count as well.
              tone={oldestOpenTone(identity.open_health_issues, identity.oldest_open_issue_days)}
              accent
              caption={
                identity.open_health_issues == null
                  ? "The issues read failed, so no age was computed — unknown, not new"
                  : identity.open_health_issues === 0
                    ? "Nothing is open, so there is no oldest. A measured absence, not a failed read."
                    : identity.oldest_open_issue_days == null
                      ? "Open issues carried no usable creation timestamp, so their age is unknown"
                      : `Warns from ${STALE_INCIDENT_DAYS} days — past that it is neglect, not a blip`
              }
            />
          </div>
        </div>
      </PanelCard>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <BreakdownPanel
          title="Sensors by health status"
          breakdown={identity.by_sensor_health}
          form="donut"
          noun="sensors"
          palette="sensorHealth"
          ariaLabel="Defender for Identity sensors by health status"
          twinId={TWIN_IDS.sensorHealth}
          since={since}
          staleAfterMs={staleAfterMs}
          truncated={identity.truncated}
          caption="no k-anonymity floor — a sensor is infrastructure, not a person"
          totalLabel="Sensors"
          emptyTitle="No sensor health to break down"
          emptyDescription="MDI answered and reported no sensor. That is not a clean bill of health — it is identity monitoring with nothing deployed behind it."
        />
        <BreakdownPanel
          title="Sensors by type"
          breakdown={identity.by_sensor_type}
          noun="sensors"
          palette="identity"
          ariaLabel="Defender for Identity sensors by sensor type"
          twinId={TWIN_IDS.sensorType}
          since={since}
          staleAfterMs={staleAfterMs}
          truncated={identity.truncated}
          caption="no k-anonymity floor"
          emptyTitle="No sensor types to show"
          emptyDescription="MDI reported no sensor, so there is no type mix to break down."
        />
        <BreakdownPanel
          title="Sensors by deployment status"
          breakdown={identity.by_deployment_status}
          noun="sensors"
          palette="identity"
          ariaLabel="Defender for Identity sensors by deployment status"
          twinId={TWIN_IDS.sensorDeployment}
          since={since}
          staleAfterMs={staleAfterMs}
          truncated={identity.truncated}
          caption="no k-anonymity floor — a sensor stuck mid-deployment is monitoring nothing"
          emptyTitle="No deployment status to show"
          emptyDescription="MDI reported no sensor, so there is no deployment state to break down."
        />
        <BreakdownPanel
          title="Open health issues by severity"
          breakdown={identity.issues_by_severity}
          form="donut"
          noun="issues"
          palette="severity"
          ariaLabel="Open Defender for Identity health issues by severity"
          twinId={TWIN_IDS.issueSeverity}
          since={since}
          staleAfterMs={staleAfterMs}
          truncated={identity.truncated}
          caption="no k-anonymity floor"
          totalLabel="Open"
          emptyTitle="No open health issue"
          emptyDescription="MDI has no open health issue to break down. A measured zero — the verdict above says whether the read succeeded."
        />
        <BreakdownPanel
          title="Open health issues by type"
          breakdown={identity.issues_by_type}
          noun="issues"
          palette="identity"
          ariaLabel="Open Defender for Identity health issues by issue type"
          twinId={TWIN_IDS.issueType}
          since={since}
          staleAfterMs={staleAfterMs}
          truncated={identity.truncated}
          caption="no k-anonymity floor — an issue type is a product fault class"
          emptyTitle="No open health issue"
          emptyDescription="MDI has no open health issue, so there is no fault-class mix to break down."
        />
      </div>
    </section>
  );
}
